import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import {
  AiOutlineMail,
  AiOutlinePhone,
  AiOutlineEnvironment,
  AiOutlineSend,
  AiOutlineLinkedin,
  AiOutlineGithub,
  AiOutlineMessage,
} from "react-icons/ai";
import { FaWhatsapp } from "react-icons/fa";
import { SiTryhackme } from "react-icons/si";

import { AppWrap, MotionWrap } from "../../wrapper";
import { client } from "../../client";
import FeedbackModal from "./Feedback";
import "./Contact.scss";
import QuickLinks from "../Footer/QuickLinks";

const Contact = () => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const { username, email, subject, message } = formData;

  const handleChangeInput = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = () => {
    // Validate that all required fields are filled
    if (!username.trim() || !email.trim() || !message.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please fill in all required fields!",
        confirmButtonColor: "#dc913a",
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Email",
        text: "Please enter a valid email address!",
        confirmButtonColor: "#dc913a",
      });
      return;
    }

    setLoading(true);

    const contact = {
      _type: "contact",
      name: username,
      email,
      subject: subject || "Contact Form Submission",
      message,
      submittedAt: new Date().toISOString(),
    };

    client
      .create(contact)
      .then(() => {
        setLoading(false);
        Swal.fire({
          icon: "success",
          title: "Message Sent!",
          text: "Thank you for reaching out! I'll get back to you soon.",
          confirmButtonColor: "#dc913a",
        });
        setFormData({ username: "", email: "", subject: "", message: "" });
      })
      .catch((err) => {
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: "Something went wrong. Please try again later or contact me directly.",
          confirmButtonColor: "#dc913a",
        });
        console.error("Contact form error:", err);
      });
  };

  const contactInfo = [
    {
      icon: <AiOutlineMail />,
      title: "Email",
      info: "momonaim01@gmail.com",
      link: "mailto:momonaim01@gmail.com",
      color: "#ea4335",
    },
    {
      icon: <FaWhatsapp />,
      title: "WhatsApp",
      info: "+212 652-182486",
      link: "https://wa.me/212652182486",
      color: "#25d366",
    },
    {
      icon: <AiOutlineEnvironment />,
      title: "Location",
      info: "Morocco",
      link: "#",
      color: "#4285f4",
    },
  ];

  const socialLinks = [
    {
      icon: <AiOutlineLinkedin />,
      name: "LinkedIn",
      link: "https://linkedin.com/in/abdelmounim-mouadili-6bb946283",
      color: "#0077b5",
    },
    {
      icon: <AiOutlineGithub />,
      name: "GitHub",
      link: "https://github.com/momonaim",
      color: "#333",
    },
    {
      icon: <SiTryhackme />,
      name: "TryHackMe",
      link: "https://tryhackme.com/p/diliabdel",
      color: "#212c42",
    },
  ];

  return (
    <>
      <div className="app__contact-header">
        <h2 className="head-text">
          Let's <span>Connect</span> & Create Something Amazing
        </h2>
        <p className="contact-subtitle">
          Have a project in mind or just want to say hello? I'd love to hear
          from you!
        </p>
      </div>

      <div className="app__contact-container">
        {/* Contact Information Section */}
        <motion.div
          className="app__contact-info"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="contact-section-title">Get in Touch</h3>

          <div className="app__contact-cards">
            {contactInfo.map((contact, index) => (
              <motion.div
                key={index}
                className="app__contact-card"
                whileHover={{ y: -5, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="contact-icon" style={{ color: contact.color }}>
                  {contact.icon}
                </div>
                <div className="contact-details">
                  <h4>{contact.title}</h4>
                  <a
                    href={contact.link}
                    target={
                      contact.link.startsWith("http") ? "_blank" : "_self"
                    }
                    rel={
                      contact.link.startsWith("http")
                        ? "noopener noreferrer"
                        : ""
                    }
                  >
                    {contact.info}
                  </a>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="app__social-links">
            <h4>Follow Me</h4>
            <div className="social-icons">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon"
                  style={{ color: social.color }}
                  whileHover={{ scale: 1.2, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                  title={social.name}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Contact Form Section */}
        <motion.div
          className="app__contact-form-container"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h3 className="contact-section-title">Send a Message</h3>

          <div className="app__contact-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="username">Name *</label>
                <input
                  id="username"
                  className="p-text"
                  type="text"
                  placeholder="Your Full Name"
                  required
                  name="username"
                  value={username}
                  onChange={handleChangeInput}
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  className="p-text"
                  type="email"
                  placeholder="your.email@example.com"
                  required
                  name="email"
                  value={email}
                  onChange={handleChangeInput}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="subject">Subject</label>
              <input
                id="subject"
                className="p-text"
                type="text"
                placeholder="What is this about?"
                name="subject"
                value={subject}
                onChange={handleChangeInput}
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                className="p-text"
                placeholder="Tell me about your project or just say hello..."
                value={message}
                name="message"
                required
                onChange={handleChangeInput}
                rows="6"
              />
            </div>

            <motion.button
              type="button"
              className="submit-btn"
              onClick={handleSubmit}
              disabled={loading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {loading ? (
                <>
                  <div className="loading-spinner"></div>
                  Sending...
                </>
              ) : (
                <>
                  <AiOutlineSend />
                  Send Message
                </>
              )}
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* Additional CTA Section */}
      <motion.div
        className="app__contact-cta"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <h3>Ready to start a conversation?</h3>
        <p>
          Whether you have a question, want to start a project, or simply want
          to connect.
        </p>
        <div className="cta-buttons">
          <a href="mailto:momonaim01@gmail.com" className="cta-btn primary">
            <AiOutlineMail />
            Email Me Directly
          </a>
          <a
            href="https://wa.me/212652182486"
            target="_blank"
            rel="noopener noreferrer"
            className="cta-btn secondary"
          >
            <FaWhatsapp />
            WhatsApp Chat
          </a>
          <motion.button
            className="cta-btn feedback"
            onClick={() => setShowFeedbackModal(true)}
            // whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <AiOutlineMessage />
            Leave Feedback
          </motion.button>
        </div>
      </motion.div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />
    </>
  );
};

export default Contact;
// export default AppWrap(
//   MotionWrap(Contact, "app__contact"),
//   "contactus",
//   "app__whitebg"
// );
